import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { User, LogOut, ExternalLink, X, Bell, Fingerprint, WifiOff, ShieldCheck, ChevronRight } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useIsOnline } from '@/hooks/useIsOnline';
import { useToast } from '@/hooks/use-toast';
import { getBiometryStatus, type BiometryStatus } from '@/lib/native/appLock';
import { ThemeToggleRows } from '@/components/theme/ThemeToggle';

// Profile sheet (P0-IA-04): identity + sign-out + settings status rows (push: built but gated on
// Firebase provisioning; biometric app-lock: live, wired in MobileShell — the row shows the device's
// real capability) + deep-links to the full desktop tools via the system browser (@capacitor/browser).
// Rendered as a fixed top-right avatar inside the native shell so it is reachable from every tab.
//
// Offline-resilience (Sam HARD REQUIREMENT): identity is read from useAuth() (the local Supabase
// session — already last-known-good offline, no fetch to cache here). The only network-ish actions
// are the "Desktop tools" deep-links (Browser.open to the live web origin) and signOut(). Offline we
// disable the deep-links (a dead page is worse than a clear message) and message it; sign-out is
// best-effort + still clears local state so the user is never stuck on a stale signed-in shell.

// TODO(Sam): confirm the production web origin for the "open in browser" desktop deep-links.
const APP_WEB_ORIGIN = 'https://www.capeberkshire.co.uk';

const DESKTOP_TOOLS: { label: string; path: string }[] = [
  { label: 'Research ops console', path: '/partner/research' },
  { label: 'Portfolio management', path: '/partner/portfoliomanagement/p' },
  { label: 'Compliance', path: '/partner/compliance' },
];

export function ProfileSheet() {
  const [open, setOpen] = useState(false);
  const { user, staffMember, signOut } = useAuth();
  const navigate = useNavigate();
  const online = useIsOnline();
  const { toast } = useToast();
  const native = Capacitor.isNativePlatform();

  // Live biometric app-lock status (null = still checking). Native-only: on web the plugin chunk is
  // never loaded and the row shows a static "native app only" state. The lock itself is always-on on
  // native (wired in MobileShell) — there is no user preference to toggle, so this row is status-only.
  const [bioStatus, setBioStatus] = useState<BiometryStatus | null>(null);
  useEffect(() => {
    if (!native || !open) return;
    let cancelled = false;
    void getBiometryStatus().then((s) => {
      if (!cancelled) setBioStatus(s);
    });
    return () => {
      cancelled = true;
    };
  }, [native, open]);

  const bioLabel = !native
    ? 'Native app only'
    : bioStatus == null
      ? 'Checking…'
      : bioStatus.biometryAvailable
        ? 'On'
        : bioStatus.deviceIsSecure
          ? 'On · device PIN'
          : 'Unavailable — no device lock';

  const name = staffMember
    ? `${staffMember.first_name} ${staffMember.last_name}`.trim() || staffMember.email
    : user?.email ?? '';
  const initials =
    (staffMember?.first_name?.[0] ?? user?.email?.[0] ?? '?').toUpperCase() +
    (staffMember?.last_name?.[0] ?? '').toUpperCase();

  const openDesktop = async (path: string) => {
    // Deep-links load the live desktop web origin — useless offline. Block + message rather than
    // open a dead/blank in-app browser tab.
    if (!online) {
      toast({
        title: "You're offline",
        description: 'Desktop tools open the full web app and need a connection. Reconnect to continue.',
      });
      return;
    }
    try {
      await Browser.open({ url: `${APP_WEB_ORIGIN}${path}` });
    } catch {
      toast({
        title: "Couldn't open desktop tools",
        description: 'The browser failed to open. Check your connection and try again.',
        variant: 'destructive',
      });
    }
  };

  // Security & devices is an in-app route (offline-aware cached read) — unlike the desktop deep-links
  // it works offline, so it is never gated on connectivity. Close the sheet, then navigate.
  const openSecurity = () => {
    setOpen(false);
    navigate('/partner/m/security');
  };

  const handleSignOut = async () => {
    setOpen(false);
    // signOut() clears local auth state (the onAuthStateChange SIGNED_OUT handler nulls user/session)
    // even if the network call to revoke the token can't reach Supabase. So we always navigate to the
    // login screen; if revocation failed offline we just note it — the session is cleared locally.
    try {
      await signOut();
    } catch {
      toast({
        title: 'Signed out locally',
        description: "You're offline, so we couldn't fully sign out on the server. You'll be signed out on this device.",
      });
    }
    // AUD-4: purge the daily audio brief (downloaded file + cached episode) on interactive sign-out.
    // Dynamic-imported + best-effort so a failure here can never block the redirect to login.
    try {
      const { purgeBriefAudio } = await import('@/hooks/useBriefAudio');
      await purgeBriefAudio();
    } catch {
      /* brief-audio purge best-effort */
    }
    navigate('/partner/login');
  };

  return (
    <>
      <button
        type="button"
        aria-label="Profile"
        onClick={() => setOpen(true)}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground shadow"
      >
        {initials}
      </button>

      {open && (
        <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true">
          <button aria-label="Close" className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-0 flex h-full w-[85%] max-w-sm flex-col bg-card shadow-2xl pt-[env(safe-area-inset-top)]">
            <div className="flex items-center justify-between border-b border-border p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
                  {initials}
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-heading font-semibold">{name}</p>
                  <p className="truncate text-xs text-muted-foreground">{staffMember?.role_level ?? 'partner'}</p>
                </div>
              </div>
              <button type="button" aria-label="Close" onClick={() => setOpen(false)} className="rounded-full p-1 text-muted-foreground hover:bg-accent">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {/* Appearance/theme is native-mobile only — on the web mobile-shell the site is always
                  light (ThemeProvider forces it), so the toggle would be a no-op. Hide it there. */}
              {Capacitor.isNativePlatform() && (
                <>
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Appearance</p>
                  <ThemeToggleRows />
                </>
              )}

              <p className="mb-2 mt-5 text-xs font-medium uppercase tracking-wide text-muted-foreground">Settings</p>
              <div className="space-y-1">
                {/* Push is fully built (src/lib/native/push.ts) but registration is hard-gated on
                    PUSH_FCM_CONFIGURED=false until Firebase (google-services.json / APNs) is
                    provisioned — so the honest label is "awaiting setup", not "coming soon". */}
                <div className="flex items-center justify-between rounded-lg px-3 py-2.5 opacity-60">
                  <span className="flex items-center gap-2 text-sm"><Bell className="h-4 w-4" /> Push notifications</span>
                  <span className="text-xs text-muted-foreground">Awaiting Firebase setup</span>
                </div>
                {/* Biometric app-lock is live on native (always-on, no user toggle — MobileShell
                    gates cold start + resume). This row reflects the device's real capability. */}
                <div className={`flex items-center justify-between rounded-lg px-3 py-2.5 ${native ? '' : 'opacity-60'}`}>
                  <span className="flex items-center gap-2 text-sm"><Fingerprint className="h-4 w-4" /> Biometric lock</span>
                  <span className="text-xs text-muted-foreground">{bioLabel}</span>
                </div>
              </div>

              <p className="mb-2 mt-5 text-xs font-medium uppercase tracking-wide text-muted-foreground">Security</p>
              <div className="space-y-1">
                <button
                  type="button"
                  onClick={openSecurity}
                  className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm hover:bg-accent"
                >
                  <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Security &amp; devices</span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>

              <p className="mb-2 mt-5 text-xs font-medium uppercase tracking-wide text-muted-foreground">Desktop tools</p>
              {!online && (
                <p className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <WifiOff className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  Offline — desktop tools need a connection.
                </p>
              )}
              <div className="space-y-1">
                {DESKTOP_TOOLS.map((t) => (
                  <button
                    key={t.path}
                    type="button"
                    onClick={() => openDesktop(t.path)}
                    disabled={!online}
                    aria-disabled={!online}
                    className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
                  >
                    <span className="flex items-center gap-2"><User className="h-4 w-4" /> {t.label}</span>
                    <ExternalLink className="h-4 w-4 text-muted-foreground" />
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t border-border p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
              <button
                type="button"
                onClick={handleSignOut}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-border py-2.5 text-sm font-medium hover:bg-accent"
              >
                <LogOut className="h-4 w-4" /> Sign out
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
